import { Component, type ErrorInfo, type ReactNode } from 'react';
import { track } from '../analytics/client';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort recovery boundary (cue-forge-drt.5). A render throw anywhere in the
 * workspace tree — e.g. a malformed ruleset reaching the thresholds/report UI —
 * would otherwise unmount the whole React tree and leave a blank white page,
 * losing the in-memory document and undo history. Placing this INSIDE the app
 * providers means the session state (and its autosave) survive the crash, so the
 * user can reload into their restored work instead of losing it silently.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for local debugging / any error reporter; not user-facing.
    console.error('Unhandled render error caught by ErrorBoundary:', error, info.componentStack);
    // Count that a crash reached the boundary — no message, stack, or component
    // path (those can carry user content); the empty-prop event is just a tally.
    track('error_boundary', {});
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="cf-errorboundary" role="alert">
        <div className="cf-errorboundary__panel">
          <h2>Something went wrong</h2>
          <p>
            The app hit an unexpected error. Your work is autosaved — reloading should restore it.
          </p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload the app
          </button>
        </div>
      </div>
    );
  }
}
